import { useState } from "react";
import { X, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI", models: ["gpt-4o", "gpt-4o-mini", "gpt-5.2", "gpt-4.1"] },
  { value: "anthropic", label: "Anthropic", models: ["claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001", "claude-4-sonnet-20250514"] },
  { value: "groq", label: "Groq", models: ["llama-3.3-70b-versatile", "mixtral-8x7b-32768", "gemma2-9b-it"] },
  { value: "openrouter", label: "OpenRouter", models: ["openai/gpt-4o", "anthropic/claude-3.5-sonnet", "meta-llama/llama-3.1-8b-instruct"] },
];

export default function ProviderModal({ open, onClose, providers, onAddProvider, onDeleteProvider }) {
  const [showForm, setShowForm] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [provider, setProvider] = useState("");
  const [modelName, setModelName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedProviderModels = PROVIDER_OPTIONS.find((p) => p.value === provider)?.models || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName || !provider || !modelName || !apiKey) return;
    setLoading(true);
    try {
      await onAddProvider({ display_name: displayName, provider, model_name: modelName, api_key: apiKey });
      setDisplayName("");
      setProvider("");
      setModelName("");
      setApiKey("");
      setShowForm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="provider-modal"
        className="bg-[#111620] border border-[#273142] shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-0 rounded-2xl max-w-lg [&>button]:hidden"
      >
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold tracking-tight">
              LLM PROVIDERS
            </DialogTitle>
            <button
              data-testid="close-provider-modal"
              onClick={() => onClose(false)}
              className="text-[#475569] hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </DialogHeader>

        <div className="p-6 pt-4 space-y-4">
          {/* Existing Providers */}
          {providers.length > 0 && (
            <div className="space-y-2">
              {providers.map((p) => (
                <div
                  key={p.id}
                  data-testid={`provider-item-${p.id}`}
                  className="flex items-center justify-between bg-[#080B10] border border-[#273142] p-3 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-2 h-2 bg-[#10B981] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.display_name}</p>
                      <p className="font-mono text-[10px] text-[#475569] uppercase">
                        {p.provider} / {p.model_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-[#475569]">{p.api_key_masked}</span>
                    <button
                      data-testid={`delete-provider-${p.id}`}
                      onClick={() => onDeleteProvider(p.id)}
                      className="text-[#475569] hover:text-[#FF3366] transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {providers.length === 0 && !showForm && (
            <div className="text-center py-8 border border-[#273142] border-dashed">
              <p className="text-[#475569] text-xs font-mono uppercase">No providers configured</p>
              <p className="text-[#475569] text-xs mt-1">Add one to start chatting</p>
            </div>
          )}

          {/* Add Form */}
          {showForm ? (
            <form onSubmit={handleSubmit} className="space-y-4 border border-[#273142] p-4 bg-[#080B10]">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
                  Display Name
                </Label>
                <Input
                  data-testid="provider-display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="My GPT-4o"
                  className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00] font-mono text-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
                  Provider
                </Label>
                <Select value={provider} onValueChange={(val) => { setProvider(val); setModelName(""); }}>
                  <SelectTrigger data-testid="provider-select-dropdown" className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00]">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111620] border-[#273142] rounded-lg">
                    {PROVIDER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-[#1C2433] focus:bg-[#1C2433] rounded-md">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
                  Model
                </Label>
                <Select value={modelName} onValueChange={setModelName}>
                  <SelectTrigger data-testid="model-select-dropdown" className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00]">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111620] border-[#273142] rounded-lg">
                    {selectedProviderModels.map((m) => (
                      <SelectItem key={m} value={m} className="text-white hover:bg-[#1C2433] focus:bg-[#1C2433] rounded-md font-mono text-xs">
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
                  API Key
                </Label>
                <div className="relative">
                  <Input
                    data-testid="provider-api-key"
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00] font-mono text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-white transition-colors"
                  >
                    {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  data-testid="save-provider-button"
                  disabled={loading}
                  className="bg-[#E5FF00] text-[#080B10] font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-[#D4EC00] transition-colors rounded-lg disabled:opacity-50"
                >
                  {loading ? "SAVING..." : "SAVE PROVIDER"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-transparent text-white border border-[#273142] hover:border-[#E5FF00] hover:text-[#E5FF00] text-xs px-4 py-2 transition-colors rounded-lg"
                >
                  CANCEL
                </button>
              </div>
            </form>
          ) : (
            <button
              data-testid="add-provider-button"
              onClick={() => setShowForm(true)}
              className="w-full bg-transparent text-white border border-[#273142] hover:border-[#E5FF00] hover:text-[#E5FF00] font-medium text-sm px-4 py-2 transition-colors flex items-center justify-center gap-2 rounded-lg"
            >
              <Plus size={14} />
              ADD PROVIDER
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
